import { Component, computed, DestroyRef, inject, input, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { ApprovedPharmacyFilters } from '../../../../pages/admin-dashboard/admin-approved-pharmacies/approved-pharmacies.model';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { PharmacySegmentVTO } from '../../../../services/pharmacy/lookup.model';
import { LookupService } from '../../../../services/pharmacy/lookup.service';
import { FilterProductSearch } from '../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { FilterMultiSelectComponent } from '../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterDateComponent } from '../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterSelectComponent } from '../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';
import { FilterSortingComponent } from '../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';
import { ApprovedPharmaciesService } from '../../../../services/pharmacy/approved-pharmacies.service';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';

@Component({
  selector: 'app-approved-ph-filters',
  imports: [
    FormsModule,
    HasScopeDirective,
    FilterProductSearch,
    FilterMultiSelectComponent,
    FilterDateComponent,
    FilterSelectComponent,
    FilterInput,
    FilterSortingComponent,
    SubmitButton
],
  templateUrl: './approved-ph-filters.html',
  styleUrl: './approved-ph-filters.scss',
  standalone: true,
})
export class ApprovedPhFilters implements OnInit {
  private pharmacyService = inject(PharmacyService);
  private locale = inject(LOCALE_ID);
  private lookupService = inject(LookupService);
  private approvedPharmaciesService = inject(ApprovedPharmaciesService);
  private destroyRef = inject(DestroyRef);

  readonly SCOPES = SCOPES;

  filters = input.required<ApprovedPharmacyFilters>();
  segments = input.required<PharmacySegmentVTO[]>();

  ownerSearchTerm = signal('');
  ownerResults = signal<{ id: number; titleAr: string; titleEn: string }[]>([]);
  ownerLoading = signal(false);
  ownerError = signal('');
  showOwnerDropdown = signal(false);

  employeeSearchTerm = signal('');
  employeeResults = signal<{ id: number; titleAr: string; titleEn: string }[]>([]);
  employeeLoading = signal(false);
  employeeError = signal('');
  showEmployeeDropdown = signal(false);

  selectedSegmentIds = signal<string[]>([]);
  showSegmentDropdown = signal(false);

  governorates = signal<LookupVTO[]>([]);
  selectedGovernorateIds = signal<string[]>([]);
  showGovernorateDropdown = signal(false);
  loadingGovernorates = signal(false);

  cities = signal<LookupVTO[]>([]);
  selectedCityId = signal<number | null>(null);
  loadingCities = signal(false);

  regions = signal<LookupVTO[]>([]);
  selectedRegionId = signal<number | null>(null);
  loadingRegions = signal(false);

  minCredit = signal<number | null>(null);
  maxCredit = signal<number | null>(null);
  creditRangeInvalid = signal(false);

  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  lastModifiedOnFrom = signal<string | null>(null);
  lastModifiedOnTo = signal<string | null>(null);

  createdDateInvalid = signal(false);
  lastModifiedDateInvalid = signal(false);

  toggleSortOrder = output();
  resetFilters = output();
  applyFilters = output<ApprovedPharmacyFilters>();
  selectFullName = output<{ id: number; fullName: string }>();
  selectEmployee = output<{ id: number; fullName: string }>();

  private governorateChange$ = new Subject<number[]>();
  private cityChange$ = new Subject<number[]>();

  isArabic() {
    return this.locale.startsWith('ar');
  }

  today(): string {
    return new Date().toISOString().split('T')[0];
  }

  ngOnInit(): void {
    this.loadGovernorates();

    this.governorateChange$
      .pipe(
        switchMap((ids) => {
          this.loadingCities.set(true);
          return this.lookupService.getCities(ids);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.cities.set(res.list);
          this.loadingCities.set(false);
        },
        error: () => this.loadingCities.set(false),
      });

    this.cityChange$
      .pipe(
        switchMap((ids) => {
          this.loadingRegions.set(true);
          return this.lookupService.getRegions(ids);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.regions.set(res.list);
          this.loadingRegions.set(false);
        },
        error: () => this.loadingRegions.set(false),
      });
  }

  loadGovernorates() {
    this.loadingGovernorates.set(true);
    this.lookupService.getGovernorates?.().subscribe({
      next: (res) => {
        this.governorates.set(res.list);
        this.loadingGovernorates.set(false);
      },
      error: () => this.loadingGovernorates.set(false),
    });
  }

  onOwnerSearchChange(value: string) {
    this.ownerSearchTerm.set(value);
    if (!value) {
      this.showOwnerDropdown.set(false);
      this.ownerResults.set([]);
      return;
    }
    this.ownerLoading.set(true);
    this.showOwnerDropdown.set(true);
    this.pharmacyService
      .pharmacyOwnerAutoComplete(value)
      .pipe(finalize(() => this.ownerLoading.set(false)))
      .subscribe({
        next: (res) =>
          this.ownerResults.set(
            (res.list as any[]).map((i) => ({ id: i.id, titleAr: i.fullName, titleEn: i.fullName })),
          ),
        error: () => this.ownerError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results'),
      });
  }

  onOwnerSelect(item: { id: number; titleAr: string; titleEn: string }) {
    this.selectFullName.emit({ id: item.id, fullName: item.titleEn });
    this.showOwnerDropdown.set(false);
  }

  onEmployeeSearchChange(value: string) {
    this.employeeSearchTerm.set(value);
    if (!value) {
      this.showEmployeeDropdown.set(false);
      this.employeeResults.set([]);
      return;
    }
    this.employeeLoading.set(true);
    this.showEmployeeDropdown.set(true);
    this.approvedPharmaciesService
      .teleSalesEmployeeAutoComplete(value)
      .pipe(finalize(() => this.employeeLoading.set(false)))
      .subscribe({
        next: (res) =>
          this.employeeResults.set(
            (res.list as any[]).map((i) => ({ id: i.id, titleAr: i.fullName, titleEn: i.fullName })),
          ),
        error: () => this.employeeError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results'),
      });
  }

  onEmployeeSelect(item: { id: number; titleAr: string; titleEn: string }) {
    this.selectEmployee.emit({ id: item.id, fullName: item.titleEn });
    this.showEmployeeDropdown.set(false);
  }

  segmentOptions() {
    return this.segments().map((s) => ({ id: String(s.id), titleAr: s.titleAr, titleEn: s.titleEn }));
  }

  toggleSegmentDropdown() {
    this.showSegmentDropdown.set(!this.showSegmentDropdown());
    this.showGovernorateDropdown.set(false);
  }

  governorateOptions() {
    return this.governorates().map((g) => ({ id: String(g.id), titleAr: g.titleAr, titleEn: g.titleEn }));
  }

  toggleGovernorateDropdown() {
    this.showGovernorateDropdown.set(!this.showGovernorateDropdown());
    this.showSegmentDropdown.set(false);
  }

  onGovernorateChange(ids: string[]) {
    this.selectedGovernorateIds.set(ids);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.cities.set([]);
    this.regions.set([]);

    if (ids.length > 0) {
      this.governorateChange$.next(ids.map(Number));
    }
  }

  cityOptions() {
    return this.cities().map((c) => ({ id: c.id, titleAr: c.titleAr, titleEn: c.titleEn }));
  }

  onCityChange(cityId: any) {
    this.selectedCityId.set(cityId ?? null);
    this.selectedRegionId.set(null);
    this.regions.set([]);

    if (cityId) {
      this.cityChange$.next([Number(cityId)]);
    }
  }

  regionOptions() {
    return this.regions().map((r) => ({ id: r.id, titleAr: r.titleAr, titleEn: r.titleEn }));
  }

  closeAllDropdowns() {
    this.showSegmentDropdown.set(false);
    this.showGovernorateDropdown.set(false);
    this.showOwnerDropdown.set(false);
    this.showEmployeeDropdown.set(false);
  }

  onContainerClick(event: Event) {
    event.stopPropagation();
  }

  sortOptions = computed(() => [
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified', labelAr: 'آخر تعديل' },
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'PHARMACY_OWNER', labelEn: 'Pharmacy Owner', labelAr: 'مالك الصيدلية' },
  ]);

  onToggleSortOrder() {
    this.toggleSortOrder.emit();
  }

  onResetFilters() {
    this.ownerSearchTerm.set('');
    this.employeeSearchTerm.set('');
    this.selectedSegmentIds.set([]);
    this.selectedGovernorateIds.set([]);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.cities.set([]);
    this.regions.set([]);
    this.minCredit.set(null);
    this.maxCredit.set(null);
    this.creditRangeInvalid.set(false);
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.lastModifiedOnFrom.set(null);
    this.lastModifiedOnTo.set(null);
    this.createdDateInvalid.set(false);
    this.lastModifiedDateInvalid.set(false);
    this.closeAllDropdowns();
    this.resetFilters.emit();
  }

  onApplyFilters() {
    const min = this.minCredit() !== null ? Number(this.minCredit()) : null;
    const max = this.maxCredit() !== null ? Number(this.maxCredit()) : null;
    const creditInvalid = min !== null && max !== null && max < min;

    const createdInvalid = !!(
      this.createdOnFrom() &&
      this.createdOnTo() &&
      this.createdOnFrom()! > this.createdOnTo()!
    );
    const lastModInvalid = !!(
      this.lastModifiedOnFrom() &&
      this.lastModifiedOnTo() &&
      this.lastModifiedOnFrom()! > this.lastModifiedOnTo()!
    );

    this.createdDateInvalid.set(createdInvalid);
    this.lastModifiedDateInvalid.set(lastModInvalid);
    this.creditRangeInvalid.set(creditInvalid);
    if (createdInvalid || lastModInvalid || creditInvalid) return;

    this.applyFilters.emit({
      pharmacyOwnerId: this.ownerSearchTerm() ? (this.filters().pharmacyOwnerId ?? -1) : undefined,
      createdOnFrom: this.createdOnFrom() ?? undefined,
      createdOnTo: this.createdOnTo() ?? undefined,
      lastModifiedOnFrom: this.lastModifiedOnFrom() ?? undefined,
      lastModifiedOnTo: this.lastModifiedOnTo() ?? undefined,
      pharmacySegmentIds: this.selectedSegmentIds().length ? this.selectedSegmentIds().map(Number) : undefined,
      minCredit: this.minCredit() ?? undefined,
      maxCredit: this.maxCredit() ?? undefined,
      governorateIds: this.selectedGovernorateIds().length ? this.selectedGovernorateIds().map(Number) : undefined,
      cityId: this.selectedCityId() ?? undefined,
      regionId: this.selectedRegionId() ?? undefined,
    });
  }
}
